import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, User, Phone, Mail, Award, DollarSign, Edit, Trash2 } from 'lucide-react';

export const RefereesPage = () => {
  const { api, user } = useAuth();
  const [referees, setReferees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReferee, setSelectedReferee] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    certifications: [],
    hourly_rate: 50.0
  });
  const [certInput, setCertInput] = useState('');

  const fetchReferees = async () => {
    try {
      const response = await api().get('/referees');
      setReferees(response.data);
    } catch (error) {
      console.error('Failed to fetch referees:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferees();
  }, [api]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedReferee) {
        await api().put(`/referees/${selectedReferee.id}`, formData);
        toast.success('Referee updated successfully');
      } else {
        await api().post('/referees', formData);
        toast.success('Referee created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchReferees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save referee');
    }
  };

  const handleDelete = async (refereeId) => {
    if (!window.confirm('Are you sure you want to delete this referee?')) return;
    try {
      await api().delete(`/referees/${refereeId}`);
      toast.success('Referee deleted successfully');
      fetchReferees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete referee');
    }
  };

  const openEditDialog = (referee) => {
    setSelectedReferee(referee);
    setFormData({
      name: referee.name,
      email: referee.email,
      phone: referee.phone,
      certifications: referee.certifications || [],
      hourly_rate: referee.hourly_rate
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedReferee(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      certifications: [],
      hourly_rate: 50.0
    });
    setCertInput('');
  };

  const addCertification = () => {
    if (certInput.trim() && !formData.certifications.includes(certInput.trim())) {
      setFormData({ ...formData, certifications: [...formData.certifications, certInput.trim()] });
      setCertInput('');
    }
  };

  const removeCertification = (cert) => {
    setFormData({ ...formData, certifications: formData.certifications.filter(c => c !== cert) });
  };

  const canEdit = user?.role === 'admin' || user?.role === 'assignor';

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="referees-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-wide">Referees</h1>
          <p className="text-muted-foreground mt-1">Manage your officiating staff</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-referee-btn" className="bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Referee
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl uppercase">{selectedReferee ? 'Edit Referee' : 'New Referee'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Full Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    required
                    data-testid="referee-name-input"
                    className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="referee@email.com"
                      required
                      data-testid="referee-email-input"
                      className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      required
                      data-testid="referee-phone-input"
                      className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Hourly Rate ($)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({ ...formData, hourly_rate: parseFloat(e.target.value) })}
                    required
                    data-testid="referee-rate-input"
                    className="bg-zinc-800 border-zinc-700 h-10 rounded-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-bold uppercase tracking-wider text-zinc-500">Certifications</Label>
                  <div className="flex gap-2">
                    <Input
                      value={certInput}
                      onChange={(e) => setCertInput(e.target.value)}
                      placeholder="e.g., NFHS Basketball"
                      data-testid="referee-cert-input"
                      className="bg-zinc-800 border-zinc-700 h-10 rounded-sm flex-1"
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCertification())}
                    />
                    <Button type="button" onClick={addCertification} variant="outline" className="border-zinc-700 h-10">
                      Add
                    </Button>
                  </div>
                  {formData.certifications.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {formData.certifications.map((cert) => (
                        <span
                          key={cert}
                          className="px-2 py-1 bg-zinc-800 text-sm rounded-sm flex items-center gap-1"
                        >
                          {cert}
                          <button
                            type="button"
                            onClick={() => removeCertification(cert)}
                            className="text-zinc-500 hover:text-white ml-1"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <Button type="submit" data-testid="referee-submit-btn" className="w-full bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-wide rounded-sm h-10">
                  {selectedReferee ? 'Update Referee' : 'Create Referee'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Referees Grid */}
      {referees.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-zinc-900/50 border border-zinc-800">
          <User className="w-12 h-12 text-zinc-600 mb-4" />
          <p className="text-zinc-500 text-lg">No referees yet</p>
          {canEdit && <p className="text-zinc-600 text-sm mt-2">Click "Add Referee" to add your first official</p>}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {referees.map((referee) => (
            <div 
              key={referee.id}
              data-testid={`referee-card-${referee.id}`}
              className="bg-zinc-900/50 border border-zinc-800 p-5 hover:border-zinc-700 transition-colors"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="p-2 bg-blue-900/20 rounded-sm">
                  <User className="w-5 h-5 text-blue-400" />
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditDialog(referee)}
                      data-testid={`edit-referee-btn-${referee.id}`}
                      className="text-zinc-400 hover:text-white h-8 w-8 p-0"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(referee.id)}
                      data-testid={`delete-referee-btn-${referee.id}`}
                      className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              <h3 className="font-heading font-bold text-lg mb-3">{referee.name}</h3>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-zinc-400">
                  <Mail className="w-4 h-4 text-zinc-500" />
                  {referee.email}
                </div>
                <div className="flex items-center gap-2 text-zinc-400">
                  <Phone className="w-4 h-4 text-zinc-500" />
                  {referee.phone}
                </div>
                <div className="flex items-center gap-2 text-emerald-400">
                  <DollarSign className="w-4 h-4" />
                  {formatCurrency(referee.hourly_rate)}/hour
                </div>
              </div>

              {referee.certifications && referee.certifications.length > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <div className="flex items-center gap-2 mb-2 text-zinc-500">
                    <Award className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-wider">Certifications</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {referee.certifications.map((cert) => (
                      <span key={cert} className="px-2 py-0.5 bg-zinc-800 text-xs rounded-sm text-zinc-300">
                        {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-zinc-800 flex justify-between text-sm">
                <span className="text-zinc-500">Games: {referee.total_games}</span>
                <span className="text-emerald-400">Earnings: {formatCurrency(referee.total_earnings)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
